import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { BENI_COMPARISON_SETS } from 'frontend-utils/utils/constants/benchmark-insight';
import type HotelSettingsCompetitorsBenchmarkCompsetBuilderReviewRoute from 'lumen/routes/hotel/settings/competitors/benchmark/compset/builder/review';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelSettingsCompetitorsBenchmarkCompsetBuilderReviewController extends Controller {
  declare model: ModelFrom<HotelSettingsCompetitorsBenchmarkCompsetBuilderReviewRoute>;

  @service declare access: Services['access'];
  @service('benchmark-insight/competitors/compset')
  declare benchmarkInsightCompetitorsCompset: Services['benchmark-insight/competitors/compset'];
  @service('benchmark-insight/competitors/desired-compset')
  declare benchmarkInsightCompetitorsDesiredCompset: Services['benchmark-insight/competitors/desired-compset'];
  @service('benchmark-insight/beni-hotels')
  declare beniHotelsService: Services['benchmark-insight/beni-hotels'];

  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNumberOfCompetitors() {
    return this.intl.t('benchmark_insight.count_hotels', {
      numberOfHotels:
        this.benchmarkInsightCompetitorsCompset.selectedProposedCompetitors
          .length,
      maxNumberOfHotels:
        this.benchmarkInsightCompetitorsCompset.selectedProposedCompset
          ?.MAX_COMPETITORS,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unusedDesiredCompetitors() {
    const { selectedProposedCompset } = this.benchmarkInsightCompetitorsCompset;

    if (!selectedProposedCompset) {
      return [];
    }

    return this.benchmarkInsightCompetitorsDesiredCompset.getUnusedDesiredCompetitors(
      [
        // @ts-expect-error Class inheritance
        selectedProposedCompset,
      ],
    );
  }

  @action onClickBack(): void {
    // Use legacyTransitionTo to avoid the parent model refreshing
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo(
      'hotel.settings.competitors.benchmark.compset.builder.proposals',
    );
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onClickSave() {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const successCallback = async () => {
      if (
        !this.beniHotelsService.availableComparisonSets.includes(
          BENI_COMPARISON_SETS.COMPSET,
        )
      ) {
        // Refetch BENI hotels data and feature flags to update the availableComparisonSets.
        // This is necessary to ensure that the user can select their newly created compset, when navigating to the Benchmark Insight product.
        await this.beniHotelsService.fetchTask.perform();
        await this.access.refetchFeatureFlags();
      }

      // Use legacyTransitionTo to avoid the parent model refreshing
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.router.legacyTransitionTo(
        'hotel.settings.competitors.benchmark.compset.index',
      );
    };

    void this.benchmarkInsightCompetitorsCompset.saveCompsetTask.perform(
      successCallback,
    );

    const desiredCompetitorIds =
      this.benchmarkInsightCompetitorsDesiredCompset.desiredCompetitors.map(
        (competitor) => competitor.id,
      );
    const numberOfSupportedWishlistHotels =
      this.benchmarkInsightCompetitorsCompset.selectedProposedCompetitors.filter(
        (competitor) => {
          return desiredCompetitorIds.includes(competitor.id);
        },
      ).length;

    this.usageAnalytics.track('Benchmark Compset Edited', {
      eventTrigger: 'compset-builder',
      type: this.benchmarkInsightCompetitorsCompset
        .selectedProposedCompsetPrioritizedCategory,
      numberOfHotels:
        this.benchmarkInsightCompetitorsCompset.selectedProposedCompetitors
          .length,
      numberOfSupportedWishlistHotels,
    });
  }

  @action onClose(): void {
    // Use legacyTransitionTo to avoid the parent model refreshing
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo(
      'hotel.settings.competitors.benchmark.compset.index',
    );

    this.usageAnalytics.track('Benchmark Compset Cancelled', {
      eventTrigger: 'compset-builder',
    });
  }
}
